import styled from "@emotion/styled";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ShareIcon from "@mui/icons-material/Share";
import NoteIcon from "@mui/icons-material/Note";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import EditIcon from "@mui/icons-material/Edit";
import { 
  Box, 
  Grid, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  Typography,
  CircularProgress,
  Pagination,
  Backdrop,
  Tooltip,
  Checkbox,
  FormControlLabel,
  InputBase,
  InputAdornment,
  IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import CancelIcon from "@mui/icons-material/Cancel";
import { DateFilter } from "./DateFilter";
import React, { useState, useCallback, useEffect, useRef } from "react";
import moment from "moment";
import { useLocation, useOutletContext } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { appApi } from "../../../apis/config";
import { StyledHeader } from "../../../styles/commonStyles";
import { ParentPageWrapper } from "../../shared/ParentPageWrapper";
import { setToastMessage } from "../../../redux/MainReducer";
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionDenied } from "../../shared/PermissionDenied";
import { CompanySelect } from "../../shared/CompanySelect";

const BoxContainer = styled(Box)(({ theme }) => ({
  padding: "2%",
  boxSizing: "border-box",
  overflowY: "scroll",
  height: "90vh",
}));

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  minWidth: 0,
  height: "35px",
  padding: 3,
  display: "flex",
  alignItems: "center",
  backgroundColor: "white",
  borderRadius: 4,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  flex: 1,
  width: "100%",
  minWidth: 0,
  paddingRight: "12px",
  "& .MuiInputBase-input": {
    width: "100%",
    minWidth: 0,
    padding: "8px 8px 8px 0",
    paddingLeft: "8px",
    color: "#000",
  },
  height: "35px",
}));

const SearchResultTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '8px',
  fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: '0.9rem',
});

const SearchResultTh = styled('th')(({ active }) => ({
  backgroundColor: '#eef2f6',
  color: '#333',
  fontWeight: 'bold',
  border: '1px solid #ccc',
  padding: '12px 10px',
  textAlign: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: '#dfe6ee',
  },
  ...(active && {
    backgroundColor: '#d0daf2',
    color: '#0d47a1',
  })
}));

const SearchResultTd = styled('td')(({ isSelected, isKeyboard, isHovered }) => ({
  border: '1px solid #ccc',
  padding: '10px 8px',
  textAlign: 'center',
  verticalAlign: 'middle',
  color: isKeyboard ? '#1a237e' : '#444',
  // Priority: selected > keyboard > hover > default
  backgroundColor: isSelected
    ? '#d0d0d0'                    // Gray: user selected for preview / clicked on it last
    : isKeyboard
    ? '#bbdefb'                    // Light blue: keyboard navigation focus
    : isHovered
    ? '#e8f4fd'                    // Very light blue: mouse hover
    : '#fff',
  transition: 'background-color 0.12s ease, color 0.12s ease',
  cursor: 'pointer',
  outline: isKeyboard ? '2px solid #1565c0' : 'none',
  outlineOffset: '-2px',
}));

export const SearchResult = () => {
  const { hasPermission, isGlobalSuperAdmin, isTenantAdmin, isSystemAdmin } = usePermissions();
  const canSearch = isGlobalSuperAdmin || hasPermission("Search: Access");
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((state) => state.mainReducer || {});
  const [openPDF, setOpenPDF] = useState(false);
  const [openShare, setOpenShare] = useState(false);
  const [selectedPictureID, setSelectedPictureID] = useState(null);
  const [shareEmail, setShareEmail] = useState("");

  // Notes thread dialog
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogDoc, setNoteDialogDoc] = useState(null); // { pictureID, companyID }
  const [notesThread, setNotesThread] = useState([]); // all notes for the picture
  console.log("🚀 ~ SearchResult ~ notesThread:", notesThread)
  const [myNoteText, setMyNoteText] = useState('');   // current user's input
  const [noteLoading, setNoteLoading] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tooltipPlacement, setTooltipPlacement] = useState('left');
  const [termsAgreed, setTermsAgreed] = useState(false);
  
  // Inline editing states
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editingNoteID, setEditingNoteID] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  // ===== THREE-STATE ROW MANAGEMENT =====
  // State 1: selectedGroupKey - user clicked a row to preview (highlighted blue)
  // State 2: keyboardGroupKey - user is navigating with keyboard arrows
  // State 3: hoveredGroupKey  - user is hovering with mouse
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);  // Preview selected
  const [keyboardGroupKey, setKeyboardGroupKey] = useState(null);  // Keyboard nav
  const [hoveredGroupKey, setHoveredGroupKey] = useState(null);    // Mouse hover

  // States for interactive sorting and clicked row selection
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  const searchParams = new URLSearchParams(location.search);
  const searchId = searchParams.get("searchId");
  let storedState = null;
  if (searchId) {
    try {
      const storedStr = localStorage.getItem(`search_${searchId}`);
      if (storedStr) {
        storedState = JSON.parse(storedStr);
      }
    } catch (e) {
      console.error("Error parsing stored search state:", e);
    }
  }

  const searchValue = (storedState && storedState.searchValue !== undefined)
    ? storedState.searchValue
    : (location?.state && location.state.searchValue !== undefined)
      ? location.state.searchValue
      : '';
  const storedWorkData = storedState?.workData || location?.state?.workData;

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightText = (text, search) => {
    return text;
  };
  
  const storedQueryParams = storedState?.queryParams || location?.state?.queryParams;
  const searchType = storedState?.searchType || location?.state?.searchType || "power";
  const storedCompanyId = storedState?.selectedCompanyId || location?.state?.selectedCompanyId;
  const tenantString = storedState?.tenantString || location?.state?.tenantString;
  const { currentCompany, setCurrentCompany, fullCompanyList = [] } = useOutletContext() || {};
  const [workDataOverride, setWorkDataOverride] = useState(null);
  const [queryOverride, setQueryOverride] = useState(null);
  const workData = workDataOverride || storedWorkData;
  const queryParams = queryOverride || storedQueryParams;
  const selectedCompanyId = currentCompany || storedCompanyId;

  const isReadOnly = React.useMemo(() => {
    if (isGlobalSuperAdmin || isTenantAdmin || isSystemAdmin) {
      return false;
    }
    const companyAccess = user?.accessList?.find(
      (acc) => Number(acc.companyID) === Number(selectedCompanyId)
    );
    if (!companyAccess || !companyAccess.levelList) {
      return true;
    }
    const nonSearchLevels = companyAccess.levelList.filter(l => {
      const resolved = l.toLowerCase().trim();
      return !resolved.includes("search");
    });
    return nonSearchLevels.length === 0;
  }, [user, selectedCompanyId, isGlobalSuperAdmin, isTenantAdmin, isSystemAdmin]);

  const [data, setData] = useState(() => {
    let initialData = storedState?.data || location?.state?.data || [];
    if (!Array.isArray(initialData)) {
      initialData = [initialData];
    }
    return initialData.filter(item => item && typeof item === 'object');
  });

  const updateDataAndCache = (updater) => {
    setData(prevData => {
      const nextData = typeof updater === 'function' ? updater(prevData) : updater;
      if (searchId) {
        try {
          const storedStr = localStorage.getItem(`search_${searchId}`);
          if (storedStr) {
            const stored = JSON.parse(storedStr);
            stored.data = nextData;
            localStorage.setItem(`search_${searchId}`, JSON.stringify(stored));
          }
        } catch (e) {
          console.error("Error updating localStorage cache:", e);
        }
      }
      return nextData;
    });
  };

  const [totalCount, setTotalCount] = useState(() => storedState?.totalCount || location?.state?.totalCount || 0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [localSearchVal, setLocalSearchVal] = useState(searchValue);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const localSearchInputRef = useRef(null);


  useEffect(() => {
    setLocalSearchVal(searchValue);
  }, [searchValue]);

  const handleLocalSearchSubmit = async () => {
    setLoading(true);
    try {
      if (searchType === 'power' && workData) {
        const updatedWorkData = {
          ...workData,
          searchQuery: localSearchVal,
          selectedCompanies: selectedCompanyId ? [selectedCompanyId] : workData.selectedCompanies,
          pageNumber: 1,
          pageSize: pageSize,
        };
        const response = await appApi.post(
          `search/power/submit?selectedCompany=${selectedCompanyId}${tenantString}`,
          updatedWorkData
        );
        const results = response.data?.results || [];
        setData(results);
        const newTotalCount = response.data?.totalCount || 0;
        setTotalCount(newTotalCount);
        setPage(1);

        // Update cache
        setWorkDataOverride(updatedWorkData);
        if (searchId) {
          localStorage.setItem(`search_${searchId}`, JSON.stringify({
            data: results,
            totalCount: newTotalCount,
            searchValue: localSearchVal,
            queryParams,
            searchType,
            selectedCompanyId,
            tenantString,
            workData: updatedWorkData
          }));
        }
      } else if (searchType === 'quick' && queryParams) {
        let updatedParams = queryParams
          .replace(/searchQuery=[^&]*/, `searchQuery=${encodeURIComponent(localSearchVal)}`)
          .replace(/selectedCompany=[^&]*/, `selectedCompany=${selectedCompanyId || ""}`)
          .replace(/pageNumber=\d+/, `pageNumber=1`);
        if (selectedCompanyId && !/selectedCompany=/.test(updatedParams)) {
          updatedParams += `&selectedCompany=${selectedCompanyId}`;
        }
        setQueryOverride(updatedParams);

        const response = await appApi.get(`search/quick/submit?${updatedParams}`);
        const results = response.data?.results || [];
        setData(results);
        const newTotalCount = response.data?.totalCount || 0;
        setTotalCount(newTotalCount);
        setPage(1);

        // Update cache
        if (searchId) {
          localStorage.setItem(`search_${searchId}`, JSON.stringify({
            data: results,
            totalCount: newTotalCount,
            searchValue: localSearchVal,
            queryParams: updatedParams,
            searchType,
            selectedCompanyId,
            tenantString,
            workData
          }));
        }
      }
    } catch (error) {
      console.error("Error executing local search:", error);
      dispatch(setToastMessage({ severity: 'error', message: 'Search failed.', messageStatus: true }));
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setLocalSearchVal("");
    const input = localSearchInputRef.current;
    if (input) input.focus();
  };

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchId = searchParams.get("searchId");
    let storedState = null;
    if (searchId) {
      try {
        const storedStr = localStorage.getItem(`search_${searchId}`);
        if (storedStr) {
          storedState = JSON.parse(storedStr);
        }
      } catch (e) {
        console.error("Error parsing stored search state:", e);
      }
    }

    let newData = storedState?.data || location?.state?.data || [];
    if (!Array.isArray(newData)) {
      newData = [newData];
    }
    setData(newData.filter(item => item && typeof item === 'object'));
    setTotalCount(storedState?.totalCount || location?.state?.totalCount || 0);
    setPage(1);
    setWorkDataOverride(null);
    setQueryOverride(null);
  }, [location?.state, location.search]);

  const handlePageChange = async (event, newPage) => {
    setPage(newPage);
    await fetchPageData(newPage, pageSize);
  };

  const fetchPageData = async (targetPage, targetPageSize) => {
    setLoading(true);
    try {
      if (searchType === 'power' && workData) {
        const updatedWorkData = {
          ...workData,
          pageNumber: targetPage,
          pageSize: targetPageSize,
          totalCount: totalCount
        };
        const response = await appApi.post(
          `search/power/submit?selectedCompany=${selectedCompanyId}${tenantString}`,
          updatedWorkData
        );
        setData(response.data?.results || []);
        if (response.data?.totalCount) {
          setTotalCount(response.data.totalCount);
        }
      } else if (searchType === 'quick' && queryParams) {
        let updatedParams = queryParams
          .replace(/pageNumber=\d+/, `pageNumber=${targetPage}`)
          .replace(/pageSize=\d+/, `pageSize=${targetPageSize}`);
        if (totalCount) {
          updatedParams += `&totalCount=${totalCount}`;
        }
        const response = await appApi.get(`search/quick/submit?${updatedParams}`);
        setData(response.data?.results || []);
        if (response.data?.totalCount) {
          setTotalCount(response.data.totalCount);
        }
      }
    } catch (error) {
      console.error("Error fetching page data:", error);
      dispatch(setToastMessage({ severity: 'error', message: 'Failed to fetch search results.', messageStatus: true }));
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (event) => {
    const nextCompanyId = event.target.value;
    if (!nextCompanyId || String(nextCompanyId) === String(selectedCompanyId)) return;
    if (setCurrentCompany) setCurrentCompany(nextCompanyId);
  };

  const previousResultCompanyRef = useRef(selectedCompanyId);

  useEffect(() => {
    if (!currentCompany) return;
    if (String(previousResultCompanyRef.current ?? "") === String(currentCompany)) return;
    previousResultCompanyRef.current = currentCompany;
    const input = localSearchInputRef.current;
    if (input) {
      input.focus();
      input.select();
    }
  }, [currentCompany]);

  // Group data by Company, then Category
  const groupedCompanies = data.reduce((acc, obj) => {
    const companyName = obj.companyName || 'Unknown Company';
    const category = obj.category || 'Unknown Category';
    
    if (!acc[companyName]) {
      acc[companyName] = {};
    }
    if (!acc[companyName][category]) {
      acc[companyName][category] = [];
    }
    acc[companyName][category].push(obj);
    return acc;
  }, {});

  // Memoized array of all group keys in order of rendering
  const orderedGroupKeys = React.useMemo(() => {
    const keys = [];
    Object.entries(groupedCompanies).forEach(([companyName, categories]) => {
      Object.entries(categories).forEach(([categoryName, items]) => {
        const criteriaNames = [];
        items.forEach(item => {
          (item.criteriaList || []).forEach(c => {
            if (c.name && c.name !== "Document Date" && !criteriaNames.includes(c.name)) {
              criteriaNames.push(c.name);
            }
          });
        });
        
        const groups = [];
        items.forEach(item => {
          const match = groups.find(g => {
            const gList = g.criteriaList.filter(c => c.name !== "Document Date");
            const iList = item.criteriaList.filter(c => c.name !== "Document Date");
            if (gList.length !== iList.length) return false;
            return gList.every((gCrit, idx) => {
              const iCrit = iList[idx];
              return iCrit && gCrit.name === iCrit.name && gCrit.value === iCrit.value;
            });
          });

          if (match) {
            match.documents.push(item);
          } else {
            groups.push({
              criteriaList: item.criteriaList || [],
              criteriaTXT: item.criteriaTXT || "",
              groupKey: `${item.companyName}_${item.category}_${item.criteriaTXT || ""}_${item.pictureID}`,
              documents: [item]
            });
          }
        });

        if (sortConfig.key && sortConfig.direction) {
          const { key, direction } = sortConfig;
          const isAsc = direction === "asc";

          groups.sort((a, b) => {
            let valA = "";
            let valB = "";

            if (criteriaNames.includes(key)) {
              const critA = a.criteriaList.find(c => c.name === key);
              const critB = b.criteriaList.find(c => c.name === key);
              valA = critA ? critA.value : "";
              valB = critB ? critB.value : "";
            } else if (key === "pictureDate") {
              valA = a.documents[0]?.pictureDate || "";
              valB = b.documents[0]?.pictureDate || "";
            } else if (key === "username") {
              valA = a.documents[0]?.username || "";
              valB = b.documents[0]?.username || "";
            } else if (key === "dnchDate") {
              valA = a.documents[0]?.dnchDate || "";
              valB = b.documents[0]?.dnchDate || "";
            }

            if (key === "pictureDate" || key === "dnchDate") {
              const dateA = valA ? new Date(valA).getTime() : 0;
              const dateB = valB ? new Date(valB).getTime() : 0;
              return isAsc ? dateA - dateB : dateB - dateA;
            }

            const strA = String(valA).toLowerCase();
            const strB = String(valB).toLowerCase();
            if (strA < strB) return isAsc ? -1 : 1;
            if (strA > strB) return isAsc ? 1 : -1;
            return 0;
          });
        }

        groups.forEach(g => {
          if (!keys.includes(g.groupKey)) {
            keys.push(g.groupKey);
          }
        });
      });
    });
    return keys;
  }, [groupedCompanies, sortConfig]);

  const renderDate = (date) => {
    return date
      ? new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "";
  };

  const handlePDFClick = async (pictureID, companyID) => {
    try {
      setLoading(true);
      const effectiveCompanyId = selectedCompanyId || companyID;
      const response = await appApi.get(`search/${searchType}/download?pictureID=${pictureID}&selectedCompany=${effectiveCompanyId}${tenantString || ""}`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfBlobUrl(url);
      setOpenPDF(true);
    } catch (error) {
      console.error("Error fetching PDF:", error);
      let errorMessage = "Failed to load PDF preview.";
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          try {
            const errorJson = JSON.parse(text);
            errorMessage = errorJson.message || errorJson.error || text;
          } catch (_) {
            if (text && text.trim().length > 0) {
              errorMessage = text.trim();
            }
          }
        } catch (blobErr) {
          console.error("Error reading error blob:", blobErr);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      dispatch(setToastMessage({ severity: 'error', message: errorMessage, messageStatus: true }));
    } finally {
      setLoading(false);
    }
  };

  const renderFormattedNote = (noteText) => {
    if (!noteText) return "";
    let html = String(noteText)
      .replace(/\\r\\n/g, '<br />')
      .replace(/\\n/g, '<br />')
      .replace(/\r\n/g, '<br />')
      .replace(/\n/g, '<br />');

    if (searchValue && searchValue.trim()) {
      const searchStr = searchValue.trim();
      const escapedSearch = escapeRegExp(searchStr);
      const parts = html.split(/(<[^>]+>)/g);
      html = parts.map(part => {
        if (part.startsWith('<') && part.endsWith('>')) {
          return part;
        }
        const textParts = part.split(new RegExp(`(${escapedSearch})`, 'gi'));
        return textParts.map(p => 
          p.toLowerCase() === searchStr.toLowerCase()
            ? `<mark style="background-color: #ffeb3b; padding: 0 2px; border-radius: 2px; color: black;">${p}</mark>`
            : p
        ).join('');
      }).join('');
    }

    return (
      <div 
        dangerouslySetInnerHTML={{ __html: html }} 
        style={{ textAlign: 'left', wordBreak: 'break-word' }}
      />
    );
  };

  const handleNoteMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.left < window.innerWidth / 2) {
      setTooltipPlacement('right');
    } else {
      setTooltipPlacement('left');
    }
  };

  const handleNoteIconClick = async (doc) => {
    const effectiveCompanyId = selectedCompanyId || doc.companyID;
    setNoteDialogDoc({ pictureID: doc.pictureID, companyID: effectiveCompanyId });
    setNoteDialogOpen(true);
    setNotesThread([]);
    setMyNoteText('');
    setNoteLoading(true);
    try {
      const res = await appApi.get(
        `search/${searchType}/notes?pictureID=${doc.pictureID}&selectedCompany=${effectiveCompanyId}${tenantString || ''}`
      );
      const fetchedNotes = res.data?.notes || [];
      setNotesThread(fetchedNotes);
      const myNote = fetchedNotes.find(n => n.isOwn);
      setMyNoteText('');
    } catch (e) {
      console.error('Error loading notes:', e);
    } finally {
      setNoteLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!noteDialogDoc) return;
    const { pictureID, companyID } = noteDialogDoc;

    if (editingNoteID) {
      if (!editingNoteText.trim()) return;
      try {
        setNoteLoading(true);
        await appApi.put(
          `search/${searchType}/note?selectedCompany=${companyID}${tenantString || ''}`,
          { pictureID, note: editingNoteText, notesID: editingNoteID }
        );
        // Refresh thread
        const res = await appApi.get(
          `search/${searchType}/notes?pictureID=${pictureID}&selectedCompany=${companyID}${tenantString || ''}`
        );
        const updatedNotes = res.data?.notes || [];
        setNotesThread(updatedNotes);
        setEditingNoteID(null);
        setEditingNoteText('');
        
        // Update noteCount and latestNote in search results (as structured array)
        const parsedLatestNotes = updatedNotes.map(n => ({
          username: n.username || 'User',
          note: n.note,
          created_at: n.created_at || ''
        }));
        updateDataAndCache(prevData => prevData.map(doc =>
          doc.pictureID === pictureID
            ? { ...doc, noteCount: updatedNotes.length, latestNote: parsedLatestNotes }
            : doc
        ));
        dispatch(setToastMessage({ severity: 'success', message: 'Note updated!', messageStatus: true }));
      } catch (error) {
        console.error('Error updating note:', error);
        dispatch(setToastMessage({ severity: 'error', message: 'Failed to update note.', messageStatus: true }));
      } finally {
        setNoteLoading(false);
      }
      return;
    }

    if (!myNoteText.trim()) return;
    try {
      setNoteLoading(true);
      await appApi.put(
        `search/${searchType}/note?selectedCompany=${companyID}${tenantString || ''}`,
        { pictureID, note: myNoteText }
      );
      // Refresh thread
      const res = await appApi.get(
        `search/${searchType}/notes?pictureID=${pictureID}&selectedCompany=${companyID}${tenantString || ''}`
      );
      const updatedNotes = res.data?.notes || [];
      setNotesThread(updatedNotes);
      setMyNoteText('');
      
      // Update noteCount and latestNote in search results (as structured array)
      const parsedLatestNotes = updatedNotes.map(n => ({
        username: n.username || 'User',
        note: n.note,
        created_at: n.created_at || ''
      }));
      
      updateDataAndCache(prevData => prevData.map(doc =>
        doc.pictureID === pictureID
          ? { ...doc, noteCount: updatedNotes.length, latestNote: parsedLatestNotes }
          : doc
      ));
      dispatch(setToastMessage({ severity: 'success', message: 'Note saved!', messageStatus: true }));
    } catch (error) {
      console.error('Error saving note:', error);
      dispatch(setToastMessage({ severity: 'error', message: 'Failed to save note.', messageStatus: true }));
    } finally {
      setNoteLoading(false);
    }
  };

  const handleShareClick = (pictureID) => {
    setSelectedPictureID(pictureID);
    setOpenShare(true);
    setTermsAgreed(false);
  };

  const handleSendShare = async () => {
    if (!shareEmail) {
      dispatch(setToastMessage({ severity: 'warning', message: 'Please enter a recipient email.', messageStatus: true }));
      return;
    }
    
    // Find the company ID associated with the selected document
    let docCompanyId = selectedCompanyId;
    if (data && Array.isArray(data)) {
      const doc = data.find(d => d.pictureID === selectedPictureID);
      if (doc && doc.companyID) {
        docCompanyId = doc.companyID;
      }
    }

    try {
      setLoading(true);
      const res = await appApi.post(`search/power/share?pictureID=${selectedPictureID}&selectedCompany=${docCompanyId || ''}`, {
        recipientEmail: shareEmail
      });
      if (res.data && res.data.success === false) {
        const errMsg = res.data.message || res.data.error || 'Failed to share document.';
        dispatch(setToastMessage({ severity: 'error', message: errMsg, messageStatus: true }));
      } else {
        dispatch(setToastMessage({ severity: 'success', message: 'Document shared successfully!', messageStatus: true }));
        setOpenShare(false);
        setShareEmail("");
      }
    } catch (error) {
      console.error("Error sharing document:", error);
      const errMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to share document.';
      dispatch(setToastMessage({ severity: 'error', message: errMsg, messageStatus: true }));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMetadata = async (group, criteriaName, newValue) => {
    if (isReadOnly) return;
    if (newValue === editingCell.value) {
      setEditingCell(null);
      return;
    }
    
    const criteriaListCopy = [...group.criteriaList];
    const targetIdx = criteriaListCopy.findIndex(c => c.name === criteriaName);
    if (targetIdx === -1) {
      setEditingCell(null);
      return;
    }
    
    const oldVal = group.criteriaList[targetIdx]?.value || "";
    criteriaListCopy[targetIdx] = {
      ...criteriaListCopy[targetIdx],
      value: newValue
    };
    
    const newCriteriaTXT = criteriaListCopy.map(c => c.value).join("^|^");
    const historyIDs = group.documents.map(d => d.historyID);
    const categoryID = group.documents[0].categoryID;
    const pictureID = group.documents[0].pictureID;

    setLoading(true);
    try {
      await appApi.put(`search/${searchType}/metadata?selectedCompany=${selectedCompanyId}${tenantString || ""}`, {
        historyIDs,
        categoryID,
        companyID: selectedCompanyId,
        oldCriteriaTXT: group.criteriaTXT,
        newCriteriaTXT,
        pictureID,
        fieldName: criteriaName,
        oldValue: oldVal,
        newValue
      });
      
      updateDataAndCache(prevData => prevData.map(doc => {
        if (historyIDs.includes(doc.historyID)) {
          const docCriteriaCopy = [...(doc.criteriaList || [])];
          const docTargetIdx = docCriteriaCopy.findIndex(c => c.name === criteriaName);
          if (docTargetIdx !== -1) {
            docCriteriaCopy[docTargetIdx] = {
              ...docCriteriaCopy[docTargetIdx],
              value: newValue
            };
          }
          return {
            ...doc,
            criteriaTXT: newCriteriaTXT,
            criteriaList: docCriteriaCopy
          };
        }
        return doc;
      }));
      
      dispatch(setToastMessage({ severity: 'success', message: 'Metadata updated successfully!', messageStatus: true }));
      setEditingCell(null);
    } catch (error) {
      console.error("Error updating metadata:", error);
      const errMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to update metadata.';
      dispatch(setToastMessage({ severity: 'error', message: errMsg, messageStatus: true }));
    } finally {
      setLoading(false);
    }
  };

  const closePDFModal = () => {
    setOpenPDF(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "";
      key = "";
    }
    setSortConfig({ key, direction });
  };

  // Helper to group items by metadata values and optionally sort them
  const getSortedMetadataGroups = (items, criteriaNames) => {
    const groups = [];
    items.forEach(item => {
      const match = groups.find(g => {
        const gList = g.criteriaList.filter(c => c.name !== "Document Date");
        const iList = item.criteriaList.filter(c => c.name !== "Document Date");
        if (gList.length !== iList.length) return false;
        return gList.every((gCrit, idx) => {
          const iCrit = iList[idx];
          return iCrit && gCrit.name === iCrit.name && gCrit.value === iCrit.value;
        });
      });

      if (match) {
        match.documents.push(item);
      } else {
        groups.push({
          criteriaList: item.criteriaList || [],
          criteriaTXT: item.criteriaTXT || "",
          groupKey: `${item.companyName}_${item.category}_${item.criteriaTXT || ""}_${item.pictureID}`,
          documents: [item]
        });
      }
    });

    if (sortConfig.key && sortConfig.direction) {
      const { key, direction } = sortConfig;
      const isAsc = direction === "asc";

      groups.sort((a, b) => {
        let valA = "";
        let valB = "";

        if (criteriaNames.includes(key)) {
          const critA = a.criteriaList.find(c => c.name === key);
          const critB = b.criteriaList.find(c => c.name === key);
          valA = critA ? critA.value : "";
          valB = critB ? critB.value : "";
        } else if (key === "pictureDate") {
          valA = a.documents[0]?.pictureDate || "";
          valB = b.documents[0]?.pictureDate || "";
        } else if (key === "username") {
          valA = a.documents[0]?.username || "";
          valB = b.documents[0]?.username || "";
        } else if (key === "dnchDate") {
          valA = a.documents[0]?.dnchDate || "";
          valB = b.documents[0]?.dnchDate || "";
        }

        if (key === "pictureDate" || key === "dnchDate") {
          const dateA = valA ? new Date(valA).getTime() : 0;
          const dateB = valB ? new Date(valB).getTime() : 0;
          return isAsc ? dateA - dateB : dateB - dateA;
        }

        const strA = String(valA).toLowerCase();
        const strB = String(valB).toLowerCase();
        if (strA < strB) return isAsc ? -1 : 1;
        if (strA > strB) return isAsc ? 1 : -1;
        return 0;
      });
    }

    return groups;
  };

  // ===== KEYBOARD NAVIGATION HANDLER =====
  // Allows user to navigate rows with Up/Down arrow keys
  // Pressing Enter on a keyboard-focused row selects it for preview
  useEffect(() => {
    const handleKeyDown = (e) => {
      const allKeys = orderedGroupKeys;
      if (!allKeys || allKeys.length === 0) return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const currentKey = keyboardGroupKey || selectedGroupKey;
        const currentIdx = currentKey ? allKeys.indexOf(currentKey) : -1;

        let nextIdx;
        if (e.key === 'ArrowDown') {
          nextIdx = currentIdx < allKeys.length - 1 ? currentIdx + 1 : 0;
        } else {
          nextIdx = currentIdx > 0 ? currentIdx - 1 : allKeys.length - 1;
        }
        setKeyboardGroupKey(allKeys[nextIdx]);

        // Auto-scroll the focused row into view
        const rowEl = document.querySelector(`[data-groupkey="${CSS.escape(allKeys[nextIdx])}"]`);
        if (rowEl) {
          rowEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      } else if (e.key === 'Enter' && keyboardGroupKey) {
        // Enter selects the keyboard-focused row as the preview row
        setSelectedGroupKey(keyboardGroupKey);
        setKeyboardGroupKey(null);
      } else if (e.key === 'Escape') {
        setKeyboardGroupKey(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [keyboardGroupKey, selectedGroupKey, orderedGroupKeys]);

  const renderPagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    if (totalPages <= 1) return null;

    const currentGroup = Math.floor((page - 1) / 10);
    const startPage = currentGroup * 10 + 1;
    const endPage = Math.min(startPage + 9, totalPages);

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mt: 3, mb: 3, flexWrap: 'wrap' }}>
        {currentGroup > 0 && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => handlePageChange(null, startPage - 1)}
            disabled={loading}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none' }}
          >
            &lt;{startPage - 1}
          </Button>
        )}

        {pageNumbers.map((p) => (
          <Button
            key={p}
            variant={p === page ? "contained" : "outlined"}
            color={p === page ? "primary" : "inherit"}
            size="small"
            onClick={() => handlePageChange(null, p)}
            disabled={loading}
            sx={{
              minWidth: '36px',
              color: '#fff',
              borderColor: p === page ? undefined : 'rgba(255,255,255,0.3)',
              backgroundColor: p === page ? '#1976d2' : 'transparent',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: p === page ? '#115293' : 'rgba(255,255,255,0.08)',
              }
            }}
          >
            {p}
          </Button>
        ))}

        {endPage < totalPages && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => handlePageChange(null, endPage + 1)}
            disabled={loading}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none' }}
          >
            &gt;{endPage}
          </Button>
        )}
        {totalCount > 1 && (
          <Typography variant="body2" sx={{ color: '#fff', ml: 2 }}>
            Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} records
          </Typography>
        )}
      </Box>
    );
  };

  if (!canSearch) {
    return (
      <ParentPageWrapper>
        <PermissionDenied message="You do not have permission to view search results. Please contact your administrator." />
      </ParentPageWrapper>
    );
  }

  return (
    <ParentPageWrapper>
      <BoxContainer>
        {new URLSearchParams(location.search).get("hideHeader") === "true" && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
            <CompanySelect
              companies={fullCompanyList}
              value={selectedCompanyId}
              onChange={handleCompanyChange}
              tone="onBlue"
            />
          </Box>
        )}
        <StyledHeader>
          {totalCount === 1 
            ? "Viewing 1 record" 
            : `Viewing ${totalCount > 0 ? `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalCount)} of ${totalCount}` : "0"} records`
          }
          {searchValue ? ` of '${searchValue}'` : ""}
        </StyledHeader>
        {new URLSearchParams(location.search).get("hideHeader") === "true" && (
          <Box sx={{ width: '100%', maxWidth: '600px', position: 'relative', mb: 3 }}>
            <Search
              style={{
                borderRadius: 4,
                borderColor: (localSearchVal.length > 0 && localSearchVal.length < 1) ? "#d32f2f" : "#bdbdbd",
                borderWidth: 1,
                borderStyle: "solid",
              }}
            >
              <StyledInputBase
                placeholder="Search"
                inputProps={{ "aria-label": "search" }}
                inputRef={localSearchInputRef}
                name="searchValue"
                value={localSearchVal}
                onChange={(e) => setLocalSearchVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && localSearchVal.trim().length >= 1) {
                    handleLocalSearchSubmit();
                  }
                }}
                autoComplete="off"
                endAdornment={
                  <InputAdornment position="end">
                    {localSearchVal && (
                      <IconButton
                        aria-label="clear search"
                        edge="end"
                        onClick={clearSearch}
                        sx={{ mr: 1 }}
                      >
                        <CancelIcon />
                      </IconButton>
                    )}
                    <IconButton
                      aria-label="submit search"
                      edge="end"
                      onClick={() => {
                        if (localSearchVal.trim().length >= 1) {
                          handleLocalSearchSubmit();
                        }
                      }}
                      disabled={localSearchVal.trim().length < 1}
                      sx={{
                        color: "#7285b3",
                        "&.Mui-disabled": {
                          color: "#bdbdbd",
                        }
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Search>
            {localSearchVal.length > 0 && localSearchVal.length < 1 && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: "#d32f2f", 
                  position: 'absolute', 
                  bottom: -18, 
                  left: 4,
                  fontSize: '10px',
                  whiteSpace: 'nowrap'
                }}
              >
                Minimum 1 character required
              </Typography>
            )}
          </Box>
        )}
        {renderPagination()}
        
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, backdropFilter: 'blur(3px)', flexDirection: 'column' }}
          open={loading}
        >
          <CircularProgress color="inherit" size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Searching...</Typography>
        </Backdrop>
        
        {data?.length > 0 && Object.keys(groupedCompanies).length > 0 ? (
          <>
            {Object.entries(groupedCompanies).map(([companyName, categories], compIdx) => (
              <Paper 
                key={`company-${compIdx}`}
                sx={{ overflow: "hidden", marginBottom: 4, borderRadius: '8px' }} 
                elevation={6}
              >
                {/* Company Header - Renders exactly once per Company */}
                <Box
                  sx={{
                    textAlign: "center",
                    padding: 2,
                    fontWeight: "bold",
                    fontSize: "1.2rem",
                    backgroundColor: "#d9d9d9",
                    color: "#222",
                    borderBottom: "1px solid #ccc"
                  }}
                >
                  {highlightText(companyName, searchValue)}
                </Box>
                
                {Object.entries(categories).map(([categoryName, items], catIdx) => {
                  // Get distinct metadata criteria names for headers (excluding Document Date)
                  const criteriaNames = [];
                  items.forEach(item => {
                    (item.criteriaList || []).forEach(c => {
                      if (c.name && c.name !== "Document Date" && !criteriaNames.includes(c.name)) {
                        criteriaNames.push(c.name);
                      }
                    });
                  });

                  const displayHeaders = criteriaNames.length > 0 ? criteriaNames : ["Criteria"];
                  const metadataGroups = getSortedMetadataGroups(items, criteriaNames);

                  return (
                    <Box key={`cat-${compIdx}-${catIdx}`} sx={{ p: 2 }}>
                      {/* Category Header - Renders exactly once per Category */}
                      <Box
                        sx={{
                          textAlign: "left",
                          padding: 1.5,
                          backgroundColor: "#f5f5f5",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          fontWeight: "bold",
                          fontSize: "1.05rem",
                          mb: 1
                        }}
                      >
                        {highlightText(categoryName, searchValue)}
                      </Box>
                      
                      {/* Unified responsive table */}
                      <Box sx={{ overflowX: "auto", border: "1px solid #ccc", borderRadius: "4px" }}>
                        <SearchResultTable>
                          <thead>
                            <tr>
                              {/* Metadata column headers */}
                              {displayHeaders.map((name) => (
                                <SearchResultTh 
                                  key={name} 
                                  rowSpan={2}
                                  active={sortConfig.key === name}
                                  onClick={() => handleSort(name)}
                                >
                                  {name} {sortConfig.key === name && (sortConfig.direction === "asc" ? "▲" : "▼")}
                                </SearchResultTh>
                              ))}
                              {/* Unified Documents - Dates header */}
                              <SearchResultTh colSpan={4} style={{ cursor: 'default' }}>
                                Documents - Dates
                              </SearchResultTh>
                            </tr>
                            <tr>
                              {/* Subheaders under Documents - Dates */}
                              <SearchResultTh style={{ width: '160px', cursor: 'default' }}>Actions</SearchResultTh>
                              <SearchResultTh 
                                active={sortConfig.key === "pictureDate"}
                                onClick={() => handleSort("pictureDate")}
                              >
                                Document Date {sortConfig.key === "pictureDate" && (sortConfig.direction === "asc" ? "▲" : "▼")}
                              </SearchResultTh>
                              <SearchResultTh 
                                active={sortConfig.key === "username"}
                                onClick={() => handleSort("username")}
                              >
                                Scanned User {sortConfig.key === "username" && (sortConfig.direction === "asc" ? "▲" : "▼")}
                              </SearchResultTh>
                              <SearchResultTh 
                                active={sortConfig.key === "dnchDate"}
                                onClick={() => handleSort("dnchDate")}
                              >
                                Scanned Date {sortConfig.key === "dnchDate" && (sortConfig.direction === "asc" ? "▲" : "▼")}
                              </SearchResultTh>
                            </tr>
                          </thead>
                          <tbody>
                            {metadataGroups.map((group, groupIdx) => {
                              return group.documents.map((doc, docIdx) => {
                                  const isFirst = docIdx === 0;
                                  const isSelected = selectedGroupKey === group.groupKey;
                                  const isKeyboard = keyboardGroupKey === group.groupKey;
                                  const isHovered = hoveredGroupKey === group.groupKey;

                                  return (
                                    <tr 
                                      key={`${group.groupKey}-${docIdx}`}
                                      data-groupkey={isFirst ? group.groupKey : undefined}
                                      onMouseEnter={() => setHoveredGroupKey(group.groupKey)}
                                      onMouseLeave={() => setHoveredGroupKey(null)}
                                      onClick={() => {
                                        setKeyboardGroupKey(null);
                                      }}
                                    >
                                      {/* Render spanned metadata columns only for the first item in the metadata group */}
                                      {isFirst && displayHeaders.map((name) => {
                                        let cellVal = "";
                                        if (criteriaNames.length > 0) {
                                          const crit = group.criteriaList.find(c => c.name === name);
                                          cellVal = crit ? crit.value : "";
                                        } else {
                                          cellVal = group.criteriaTXT;
                                        }
                                        
                                        const isEditing = editingCell && editingCell.groupKey === group.groupKey && editingCell.criteriaName === name;

                                        return (
                                          <SearchResultTd
                                            key={name}
                                            rowSpan={group.documents.length}
                                            isSelected={isSelected}
                                            isKeyboard={isKeyboard}
                                            isHovered={isHovered}
                                            style={{ fontWeight: '600', textAlign: 'left' }}
                                            onDoubleClick={(e) => {
                                              e.stopPropagation();
                                              if (!isReadOnly) {
                                                setEditingCell({
                                                  groupKey: group.groupKey,
                                                  criteriaName: name,
                                                  value: cellVal
                                                });
                                                setEditValue(cellVal);
                                              }
                                            }}
                                            title={!isReadOnly ? "Double click to edit" : undefined}
                                          >
                                            {isEditing ? (
                                              <TextField
                                                size="small"
                                                autoFocus
                                                fullWidth
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onBlur={() => handleSaveMetadata(group, name, editValue)}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    handleSaveMetadata(group, name, editValue);
                                                  } else if (e.key === 'Escape') {
                                                    setEditingCell(null);
                                                  }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                sx={{ backgroundColor: '#fff', borderRadius: '4px' }}
                                              />
                                            ) : (
                                              highlightText(cellVal, searchValue)
                                            )}
                                          </SearchResultTd>
                                        );
                                      })}
                                      
                                      {/* Action cell */}
                                      <SearchResultTd 
                                        isSelected={isSelected}
                                        isKeyboard={isKeyboard}
                                        isHovered={isHovered}
                                        style={{ minWidth: '150px' }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                          {/* Larger PDF Icon & Page Count display */}
                                          <PictureAsPdfIcon 
                                            style={{ margin: "0 8px", color: "#1976d2", fontSize: 26, cursor: 'pointer' }} 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedGroupKey(group.groupKey);
                                              setKeyboardGroupKey(null);
                                              handlePDFClick(doc.pictureID, doc.companyID);
                                            }}
                                          />
                                          <Typography 
                                            variant="body2" 
                                            sx={{ fontWeight: 'bold', display: 'inline', mr: 1, fontSize: '0.95rem', color: '#1976d2' }}
                                          >
                                            ({doc.pageCount || 1})
                                          </Typography>

                                          {/* Note icon — colored if any notes exist */}
                                          <Tooltip
                                            title={
                                              doc.noteCount > 0 ? (
                                                <Box sx={{ backgroundColor: '#ffffcc', color: '#000', border: '1px solid #ccc', borderRadius: '4px', maxWidth: 350, maxHeight: 220, overflowY: 'auto', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)' }}>
                                                  <Box sx={{ fontWeight: 'bold', borderBottom: '1px solid #e6b800', pb: 0.5, mb: 0.5, textAlign: 'center', backgroundColor: '#ffd700', p: 0.5, color: '#333', fontSize: '0.85rem', position: 'sticky', top: 0, zIndex: 1 }}>
                                                    Notes ({doc.noteCount})
                                                  </Box>
                                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                                                    {(() => {
                                                       let list = [];
                                                       if (Array.isArray(doc.latestNote)) {
                                                         list = doc.latestNote;
                                                       } else if (typeof doc.latestNote === 'string' && doc.latestNote.trim().length > 0) {
                                                         try {
                                                           const parsed = JSON.parse(doc.latestNote);
                                                           if (Array.isArray(parsed)) {
                                                             list = parsed;
                                                           }
                                                         } catch (e) {}
                                                         
                                                         if (list.length === 0) {
                                                           list = doc.latestNote.split(' |n| ').map(noteStr => {
                                                             const dateSplit = noteStr.split('|d|');
                                                             const mainPart = dateSplit[0] || "";
                                                             const created_at = dateSplit[1] || "";
                                                             
                                                             const colonIdx = mainPart.indexOf(': ');
                                                             let username = "User";
                                                             let note = mainPart;
                                                             if (colonIdx > 0) {
                                                               username = mainPart.substring(0, colonIdx).trim();
                                                               note = mainPart.substring(colonIdx + 2);
                                                             }
                                                             return { username, note, created_at };
                                                           });
                                                         }
                                                       }
                                                       return list;
                                                     })().map((nObj, idx, arr) => {
                                                       const author = nObj.username || "User";
                                                       const content = nObj.note || "";
                                                       const datePart = nObj.created_at || "";
                                                       
                                                       let formattedDate = "";
                                                        if (datePart) {
                                                          try {
                                                            const m = moment.utc(datePart);
                                                            if (m.isValid()) {
                                                              formattedDate = m.local().format('MMM D, YYYY, h:mm A');
                                                            }
                                                          } catch (e) {}
                                                        }
                                                       return (
                                                         <Box key={idx} sx={{ pb: idx < arr.length - 1 ? 1 : 0, borderBottom: idx < arr.length - 1 ? '1px dashed #e6b800' : 'none' }}>
                                                           <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: '#555', fontSize: '0.72rem' }}>
                                                             {author} {formattedDate ? `(${formattedDate})` : ""}
                                                           </Typography>
                                                           <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#111', whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                                                             {content}
                                                           </Typography>
                                                         </Box>
                                                       );
                                                     })}
                                                  </Box>
                                                </Box>
                                              ) : isReadOnly ? 'No notes' : 'Add note'
                                            }
                                            componentsProps={{
                                              tooltip: {
                                                sx: doc.noteCount > 0 ? {
                                                  backgroundColor: 'transparent',
                                                  boxShadow: 'none',
                                                  p: 0,
                                                } : {}
                                              }
                                            }}
                                            interactive
                                            placement={tooltipPlacement}
                                          >
                                            {doc.noteCount > 0 ? (
                                              <NoteIcon
                                                style={{ margin: "0 8px", color: "#ed6c02", fontSize: 22, cursor: 'pointer' }}
                                                onMouseEnter={handleNoteMouseEnter}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleNoteIconClick(doc);
                                                }}
                                              />
                                            ) : (
                                              <NoteAddIcon
                                                style={{ margin: "0 8px", color: isReadOnly ? "#ccc" : "#90a4ae", fontSize: 22, cursor: isReadOnly ? 'default' : 'pointer' }}
                                                onMouseEnter={handleNoteMouseEnter}
                                                onClick={(e) => {
                                                  if (isReadOnly) return;
                                                  e.stopPropagation();
                                                  handleNoteIconClick(doc);
                                                }}
                                              />
                                            )}
                                          </Tooltip>

                                          <ShareIcon 
                                            style={{ margin: "0 8px", color: "#2e7d32", fontSize: 20, cursor: 'pointer' }} 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleShareClick(doc.pictureID);
                                            }}
                                          />
                                        </Box>
                                      </SearchResultTd>
                                      
                                      {/* Document Details cells */}
                                      <SearchResultTd 
                                        isSelected={isSelected}
                                        isKeyboard={isKeyboard}
                                        isHovered={isHovered}
                                      >
                                        {renderDate(doc.pictureDate)}
                                      </SearchResultTd>
                                      <SearchResultTd 
                                        isSelected={isSelected}
                                        isKeyboard={isKeyboard}
                                        isHovered={isHovered}
                                      >
                                        {highlightText(doc.username || "external user", searchValue)}
                                      </SearchResultTd>
                                      <SearchResultTd 
                                        isSelected={isSelected}
                                        isKeyboard={isKeyboard}
                                        isHovered={isHovered}
                                      >
                                        {renderDate(doc.dnchDate)}
                                      </SearchResultTd>
                                    </tr>
                                  );
                                });
                              })}
                          </tbody>
                        </SearchResultTable>
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            ))}
            
            {renderPagination()}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <StyledHeader style={{ fontSize: 28 }}>No Data Found</StyledHeader>
          </Box>
        )}
      </BoxContainer>

      {/* PDF Preview Modal */}
      <Dialog 
        open={openPDF} 
        onClose={closePDFModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#eeeeee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          PDF Preview
          <Button onClick={closePDFModal} color="error">Close</Button>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '80vh', p: 0 }}>
          {pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              title="PDF Preview"
              width="100%"
              height="800px"
              style={{ border: 'none' }}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={openShare} onClose={() => setOpenShare(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#eeeeee' }}>Share Document</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, mt: 2 }}>
            A secure, expiring one-time link will be sent to the recipient's email.
          </Typography>
          <TextField
            fullWidth
            label="Recipient Email"
            variant="outlined"
            value={shareEmail}
            onChange={(e) => setShareEmail(e.target.value)}
            disabled={loading}
            sx={{ mb: 3 }}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={termsAgreed} 
                onChange={(e) => setTermsAgreed(e.target.checked)} 
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I <strong>{user?.userFullName || 'User'}</strong> agree to the <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 'bold' }}>Terms of Service</a>.
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenShare(false)} disabled={loading}>Cancel</Button>
          <Button 
            onClick={handleSendShare} 
            variant="contained" 
            color="primary"
            disabled={loading || !shareEmail || !termsAgreed}
          >
            {loading ? <CircularProgress size={24} /> : "Send Link"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Note Dialog */}
      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#eeeeee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Notes
          <Button onClick={() => setNoteDialogOpen(false)} color="primary">Close</Button>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          {noteLoading && notesThread.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          ) : notesThread.length > 0 ? (
            <Box sx={{ mt: 2, mb: 2, pr: 1, maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {notesThread.map((n) => {
                const isEditingThisNote = editingNoteID === n.notesID;
                return (
                  <Box key={n.notesID} sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: n.isOwn ? '#fff8e1' : '#f5f5f5',
                    border: n.isOwn ? '1px solid #ffe082' : '1px solid #e0e0e0',
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: n.isOwn ? '#e65100' : '#555' }}>
                        {n.isOwn ? 'You' : (n.username || 'User')}
                        {n.created_at ? (() => {
                          try {
                            const m = moment.utc(n.created_at);
                            if (m.isValid()) {
                              return ` (${m.local().format('MMM D, YYYY, h:mm A')})`;
                            }
                          } catch (e) {}
                          return "";
                        })() : ""}
                      </Typography>
                      {!!n.isOwn && !isEditingThisNote && (
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setEditingNoteID(n.notesID);
                            setEditingNoteText(n.note);
                          }}
                          sx={{ p: 0.2, color: '#e65100' }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                    {isEditingThisNote ? (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          sx={{ backgroundColor: '#fff', mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button size="small" variant="outlined" onClick={() => setEditingNoteID(null)}>Cancel</Button>
                          <Button size="small" variant="contained" color="primary" onClick={handleSaveNote} disabled={noteLoading || !editingNoteText.trim()}>Save</Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ color: '#333' }}>{renderFormattedNote(n.note)}</Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : null}
          {!isReadOnly && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                Add note:
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Write a note..."
                variant="outlined"
                value={myNoteText}
                onChange={(e) => setMyNoteText(e.target.value)}
                disabled={noteLoading}
                size="small"
              />
            </Box>
          )}
        </DialogContent>
        {!isReadOnly && (
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setNoteDialogOpen(false)} disabled={noteLoading}>Cancel</Button>
            <Button
              onClick={handleSaveNote}
              variant="contained"
              color="primary"
              disabled={noteLoading || (!editingNoteID && !myNoteText.trim()) || (editingNoteID && !editingNoteText.trim())}
            >
              {noteLoading ? <CircularProgress size={22} /> : (editingNoteID ? 'Save' : 'Add Note')}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* DIALOG FOR ADVANCED SEARCH - CENTERED */}
      <Dialog
        open={advancedSearchOpen}
        onClose={() => setAdvancedSearchOpen(false)}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "95vw",
            maxHeight: "95vh",
            width: "auto",
            height: "auto",
            margin: "auto",
            borderRadius: "8px",
            overflow: "auto",
          }
        }}
      >
        <DateFilter 
          searchValue={localSearchVal} 
          setSearchValue={setLocalSearchVal} 
          setOpen={setAdvancedSearchOpen}
          currentCompany={selectedCompanyId}
          tenantString={tenantString}
        />
      </Dialog>
    </ParentPageWrapper>
  );
};